import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { getCoachAttendanceRollupsForGroups, getCoachClasses, getMeCoach } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { formatCoachAttendanceRollupLine } from '@/lib/student-group-utils';
import { isCoach, isEdgeCoach } from '@airreading/common';
import {
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  LearningPathNames,
  diagnosticLearningPathNames,
  practiceLearningPathNames,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import CoachStudentGroup from '../my-availability/-components/student-group';
import { RemoveStudentGroupModal } from './-components/remove-student-group-modal';

export const Route = createFileRoute('/_layout/my-student-groups/')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  component: Page,
});

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load student groups. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <MyStudentGroupsPage />
    </InfoPageSuspenseBoundary>
  );
}

function MyStudentGroupsPage() {
  const { data: profile } = useSuspenseQuery(getMeCoach());
  const canRemoveStudentGroup = useFeatureFlag('teacher-remove-student-group', false);

  const { data: classes } = useSuspenseQuery({
    ...getCoachClasses(profile.id, true),
  });

  const studentGroupIds = (classes ?? []).map((c) => c.id);
  const { data: attendanceMap = {}, isLoading: isAttendanceLoading } = useQuery(
    getCoachAttendanceRollupsForGroups({ coachId: profile.id, studentGroupIds }),
  );

  const excludedLearningPathNames: string[] = [
    ...diagnosticLearningPathNames,
    ...practiceLearningPathNames,
    LearningPathNames['Read Aloud'],
    LearningPathNames['Assigned GLA'],
  ];

  const filteredClasses = classes.filter((classData) => {
    const isRemoved = classData.teacherPortalStatus === 'removed';
    const hasNextLesson = classData.nextLesson != null;
    const isExcludedLearningPath = excludedLearningPathNames.includes(classData.learningPath?.name);
    return (isRemoved || hasNextLesson) && !isExcludedLearningPath;
  });

  return (
    <div className="max-md:-mx-3 max-md:px-3">
      <InfoPage headerText="My student groups" data-testid="my-student-groups-header">
        {filteredClasses.length === 0 && <p className="text-muted-foreground">No student groups are assigned yet.</p>}
        {filteredClasses.map((classData, idx) => (
          <CoachStudentGroup
            key={classData.id}
            classData={classData}
            teacherTimeZone={profile.timeZone}
            headerActions={
              classData.teacherPortalStatus === 'removed' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  className="border border-[#A5A3A3] text-[#A5A3A3] disabled:opacity-100"
                >
                  Removed
                </Button>
              ) : canRemoveStudentGroup ? (
                <RemoveStudentGroupModal
                  classData={classData}
                  requesterId={profile.id}
                  teacherTimeZone={profile.timeZone}
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
                      data-testid={`my-student-group-${idx}-remove`}
                    >
                      {classData.hasPendingReassignmentRequest ? 'Pending removal' : 'Remove'}
                    </Button>
                  }
                />
              ) : undefined
            }
            attendanceText={
              isAttendanceLoading ? (
                <LoaderIcon className="w-5 h-5" />
              ) : (
                (() => {
                  const { id, v1AbsenceText } = classData;
                  const coachAttendance = formatCoachAttendanceRollupLine(attendanceMap[id]) ?? '-';

                  if (!v1AbsenceText) {
                    return coachAttendance;
                  }

                  const isPerfectAttendance =
                    coachAttendance === CoachAttendanceStatusLabelMap[CoachAttendanceStatus.Perfect];

                  return isPerfectAttendance ? (
                    v1AbsenceText
                  ) : (
                    <div className="flex flex-col">
                      <div>{coachAttendance}</div>
                      <div>{v1AbsenceText}</div>
                    </div>
                  );
                })()
              )
            }
            data-testid={`my-student-group-${idx}`}
          />
        ))}
      </InfoPage>
    </div>
  );
}
